import { Platform, Channel, TaskType, TaskValue } from './taskTypes'

/** ---------- Base checklists used to build catalog ---------- */
export const DEFAULT_UPDATE_TYPES: Record<string, string[]> = {
  Search: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
  Social: ['Performance', 'Ad Copy', 'Website', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Display: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Video: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  SEO: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours'],
  Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}

export const DEFAULT_BUDGET_TYPES: Record<string, string[]> = {
  Search: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  Social: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  Display: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  Video: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  SEO: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  Website: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  Integration: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
}
export const DEFAULT_CREATE_TYPES: Record<string, string[]> = {
  Search: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaigns', 'Other'],
  Social: ['Performance', 'Ad Copy', 'Website', 'Audience', 'Targeting', 'Campaigns', 'Placements', 'Other'],
  Display: ['Performance', 'Audience', 'Targeting', 'Campaigns', 'Placements', 'Other'],
  Video: ['Performance', 'Audience', 'Targeting', 'Campaigns', 'Placements', 'Other'],
  SEO: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours', 'Campaign'],
  Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}
export const DEFAULT_INTEGRATION_TYPES: Record<string, string[]> = {
  Search: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
  Social: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking', 'Meta', 'Linkedin'],
  Display: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other', 'Campaign'],
  Video: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other', 'Campaign'],
  SEO: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours'],
  Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}
export const DEFAULT_REVIEW_TYPES: Record<string, string[]> = {
  Search: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
  Social: ['Performance', 'Ad Copy', 'Website', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Display: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Video: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  SEO: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours'],
  Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}
export const DEFAULT_ONBOARDING_TYPES: Record<string, string[]> = {
  Search: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
  Social: ['Performance', 'Ad Copy', 'Website', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Display: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  Video: ['Performance', 'Audience', 'Targeting', 'Campaign Settings', 'Placements', 'Other'],
  SEO: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours'],
  Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}
export const MARKETING_PLATFORMS = ['Google', 'Meta', 'SEO', 'Microsoft']
/** ---------- Helpers to craft TaskValue arrays ---------- */
const withSubs = (base: TaskType, subs: string[] | undefined, labelPrefix?: string): TaskValue[] => {
  if (!subs || subs.length === 0) return [{ base, label: labelPrefix ?? base }]
  return subs.map(s => ({ base, sub: s, label: labelPrefix ? `${labelPrefix} — ${s}` : `${base} — ${s}` }))
}

export const standardTaskValuesForChannel = (channel: Channel): TaskValue[] => {
  const updates = withSubs('updates', DEFAULT_UPDATE_TYPES[channel], 'Updates')
  const budget = withSubs('budget', DEFAULT_BUDGET_TYPES[channel], 'Budget')
  const create = withSubs('createSetup', DEFAULT_CREATE_TYPES[channel], 'Create | Setup')
  const review = withSubs('review', DEFAULT_REVIEW_TYPES[channel], 'Review | Audit')
  const integration = withSubs('integration', DEFAULT_INTEGRATION_TYPES[channel], 'Integration')
  const approv = withSubs('adApprovals', [], 'Ad Approvals')
  return [...updates, ...budget, ...create, ...review, ...integration, ...approv]
}

/** ---------- Catalog & overrides that drive the two-pane tree ---------- */
export const TASK_CATALOG: Partial<Record<Platform, Partial<Record<Channel, TaskValue[]>>>> = {
  Google: {
    Search: standardTaskValuesForChannel('Search'),
    Display: standardTaskValuesForChannel('Display'),
    SEO: standardTaskValuesForChannel('SEO'),
  },
  Meta: { Social: standardTaskValuesForChannel('Social'), Video: standardTaskValuesForChannel('Video') },
  Microsoft: { Social: standardTaskValuesForChannel('Social'), Search: standardTaskValuesForChannel('Search') },
  LinkedIn: { Social: standardTaskValuesForChannel('Social') },
  Simplifi: { Display: standardTaskValuesForChannel('Display'), Video: standardTaskValuesForChannel('Video') },
  YouTube: { Video: standardTaskValuesForChannel('Video') },
  GTM: { Integration: standardTaskValuesForChannel('Integration') },
  GA4: { Integration: standardTaskValuesForChannel('Integration') },
  Website: { Website: standardTaskValuesForChannel('Website') },
  SEO: { SEO: standardTaskValuesForChannel('SEO') },
}

type ProductOverrideKey = { title?: string; subcategory?: string | null }

const OVERRIDES: Array<{
  when: (p: ProductOverrideKey) => boolean
  add?: TaskValue[]
  replace?: TaskValue[]
  removeBases?: string[]
}> = [
  {
    when: p => /remarketing/i.test(p.title ?? '') || /remarket/i.test(p.subcategory ?? ''),
    add: [{ base: 'updates' as any, sub: 'Pixel Audit', label: 'Updates — Pixel Audit' }],
  },
]

/** ---------- Tree builder consumed by TaskTypeTwoPane ---------- */
const deriveTaskValues = (platform: Platform | '', channel: Channel | '', product?: any): TaskValue[] => {
  const base = (platform && channel && TASK_CATALOG[platform]?.[channel]) || []
  if (!product) return base
  return OVERRIDES.reduce((acc, rule) => {
    if (!rule.when(product)) return acc
    let next = [...acc]
    if (rule.removeBases?.length) next = next.filter(v => !rule.removeBases!.includes(v.base))
    if (rule.replace?.length) {
      const basesToReplace = new Set(rule.replace.map(v => v.base))
      next = next.filter(v => !basesToReplace.has(v.base)).concat(rule.replace)
    }
    if (rule.add?.length) next = next.concat(rule.add)
    return next
  }, base)
}

export const taskTypeTreeFor = (
  platform: Platform | null,
  channel: Channel | null,
  product?: any
): Record<string, string[]> => {
  const values = deriveTaskValues(platform, channel, product)
  const tree: Record<string, string[]> = {}
  for (const v of values) {
    if (!tree[v.base]) tree[v.base] = []
    if (v.sub) tree[v.base].push(v.sub)
  }
  for (const k of Object.keys(tree)) {
    tree[k] = Array.from(new Set(tree[k]))
  }
  return tree
}

/** ---------- Still used elsewhere in this flow (OrgRows) ---------- */
export const ORG_TWO_PANE_TREE = {
  updates: [
    'Performance',
    'Ad Copy',
    'Website',
    'Keywords',
    'Search Terms',
    'Audience',
    'Targeting',
    'Campaign Settings',
    'Placements',
    'Other',
  ],
  budget: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
  createSetup: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
  newClientOnboard: ['Quality Assurance', 'Kickoff Prep', 'Other'],
  review: ['Website', 'Keywords / Keyword Rankings', 'Google Business Profile', 'NAP / Hours'],
  campaignChecklists: ['Display – Standard'],
  adApprovals: ['Search Ads', 'Social Ads', 'Display Ads', 'Video Ads'],
  integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
}
